import { DefaultTheme, ThemeProvider } from "@react-navigation/native";
import { Stack, useLocalSearchParams, usePathname } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { useEffect } from "react";
import SheetProvider from "@/elements/components/shared/sheet-provider";
import "../global.css";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexQueryClient, convexQuery } from "@convex-dev/react-query";
import { Ionicons } from "@expo/vector-icons";
import {
  Geologica_100Thin,
  Geologica_200ExtraLight,
  Geologica_300Light,
  Geologica_400Regular,
  Geologica_500Medium,
  Geologica_600SemiBold,
  Geologica_700Bold,
  Geologica_800ExtraBold,
  Geologica_900Black,
} from "@expo-google-fonts/geologica";
import {
  ZillaSlab_300Light,
  ZillaSlab_400Regular,
  ZillaSlab_500Medium,
  ZillaSlab_600SemiBold,
  ZillaSlab_700Bold,
} from "@expo-google-fonts/zilla-slab";
import * as Sentry from "@sentry/react";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ConvexReactClient } from "convex/react";
import { useFonts } from "expo-font";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import Toast from "react-native-toast-message";
import AdditionalLearningSheet from "@/elements/components/sheets/additional-learning-sheet";
import AssignTimeSlotSheet from "@/elements/components/sheets/assign-time-slot-sheet";
import BufferTimeConflictSheet from "@/elements/components/sheets/buffer-time-conflict-sheet";
import ChildrenSelectSheet from "@/elements/components/sheets/children-select-sheet";
import DownloadStudyMaterialSheet from "@/elements/components/sheets/download-study-material-sheet";
import LessonPriceListSheet from "@/elements/components/sheets/lesson-price-list-sheet";
import LessonSheet from "@/elements/components/sheets/lesson-sheet";
import LocationSheet from "@/elements/components/sheets/location-sheet";
import MultiSelectSheet from "@/elements/components/sheets/multi-select-sheet";
import ParentConnectSheet from "@/elements/components/sheets/parent-connect-sheet";
import ParentLessonSheet from "@/elements/components/sheets/parent-lesson-sheet";
import ReferenceInfoSheet from "@/elements/components/sheets/reference-info-sheet";
import SingleSelectSheet from "@/elements/components/sheets/single-select-sheet";
import TimeSlotsSelectSheet from "@/elements/components/sheets/time-slots-sheet";
import TimezoneSearchSheet from "@/elements/components/sheets/timezone-search-sheet";
import TutorLessonSheet from "@/elements/components/sheets/tutor-lesson-sheet";
import { toastConfig } from "@/elements/components/ui/toast-config";
import { api } from "@/elements/components/utils/convex-api";
import { apiKey, options } from "@/elements/components/utils/posthog";
import { getQueryClient } from "@/elements/components/utils/react-query-client";
import { initSentry } from "@/elements/components/utils/sentry";
import { AuthProvider } from "@/elements/context/auth";
import { TimezoneProvider } from "@/elements/context/timezone";

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary,
} from "expo-router";

initSentry();

const convexClient = new ConvexReactClient(process.env.EXPO_PUBLIC_CONVEX_URL!);
const convexQueryClient = new ConvexQueryClient(convexClient);
const queryClient = getQueryClient(convexQueryClient);
convexQueryClient.connect(queryClient);

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

function RootLayout() {
  const [loaded, error] = useFonts({
    Geologica_100Thin,
    Geologica_200ExtraLight,
    Geologica_300Light,
    Geologica_400Regular,
    Geologica_500Medium,
    Geologica_600SemiBold,
    Geologica_700Bold,
    Geologica_800ExtraBold,
    Geologica_900Black,
    ZillaSlab_300Light,
    ZillaSlab_400Regular,
    ZillaSlab_500Medium,
    ZillaSlab_600SemiBold,
    ZillaSlab_700Bold,
    ...Ionicons.font,
  });

  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      if (
        event.reason instanceof TypeError &&
        event.reason.message.includes("Failed to fetch")
      ) {
        event.preventDefault();
        const errorId = `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
        Sentry.captureException(event.reason, {
          level: "warning",
          tags: {
            errorId,
            source: "unhandled-rejection",
            errorType: "network",
          },
        });
      }
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  // Expo Router uses Error Boundaries to catch errors in the navigation tree.
  useEffect(() => {
    if (error) throw error;
  }, [error]);

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded]);

  if (!loaded) {
    return null;
  }

  return <RootLayoutNav />;
}

function UserTracking() {
  const posthog = usePostHog();
  const { data: currentUser } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {}),
  );

  useEffect(() => {
    posthog?.identify(currentUser?._id, {
      name: currentUser?.name,
      email: currentUser?.email,
    });
  }, [currentUser, posthog]);

  return <></>;
}

function SentryUserTracking() {
  const { data: currentUser } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {}),
  );

  useEffect(() => {
    if (currentUser) {
      Sentry.setUser({
        id: currentUser._id,
        username: currentUser.name,
        email: currentUser.email,
      });
    }
  }, [currentUser]);

  return null;
}

function ScreenTracking() {
  const posthog = usePostHog();
  const pathname = usePathname();
  const params = useLocalSearchParams();

  useEffect(() => {
    posthog?.capture("screen_view", {
      screen: pathname,
      properties: params,
    });
  }, [pathname, params, posthog]);

  return <></>;
}

function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  if (__DEV__) {
    return children;
  }

  return (
    <PostHogProvider apiKey={apiKey} options={options}>
      {children}
    </PostHogProvider>
  );
}

function RootLayoutNav() {
  return (
    <PostHogProviderWrapper>
      <ConvexAuthProvider client={convexClient}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider value={DefaultTheme}>
            <AuthProvider>
              <TimezoneProvider>
                <SheetProvider>
                  <Stack screenOptions={{ headerShown: false }} />
                  <MultiSelectSheet />
                  <SingleSelectSheet />
                  <ChildrenSelectSheet />
                  <LessonSheet />
                  <ParentLessonSheet />
                  <AssignTimeSlotSheet />
                  <TutorLessonSheet />
                  <AdditionalLearningSheet />
                  <ReferenceInfoSheet />
                  <DownloadStudyMaterialSheet />
                  <TimeSlotsSelectSheet />
                  <LocationSheet />
                  <LessonPriceListSheet />
                  <ParentConnectSheet />
                  <BufferTimeConflictSheet />
                  <TimezoneSearchSheet />
                  <Toast config={toastConfig} />
                  <ScreenTracking />
                  <UserTracking />
                  <SentryUserTracking />
                </SheetProvider>
              </TimezoneProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ConvexAuthProvider>
    </PostHogProviderWrapper>
  );
}

export default RootLayout;
